import React, { useState, useEffect, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { db } from "../../../../firebase";
import { ref, onValue, update, remove, get } from "firebase/database";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme,
  Grid,
  InputAdornment,
  IconButton,
  OutlinedInput,
  Stack,
  Card,
  Typography,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik } from "formik";

import * as yup from "yup";
import { tokens } from "../../../../theme";
import {
  getTokenRefresh,
  verifyExpireTokenRefresh,
} from "../../../../helpers/token/refreshToken";
import SnackBar from "../../../../components/snackbar/SnackBar";
import {
  createBookingNow,
  updateDataBookingNow,
} from "../../../../api/booking/bookingNow";
import { getDriverAndVehicle } from "../../../../api/users/driver/driver";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { formatterTimePicker } from "../../../../helpers/date/formatterTimePicker";
import { dateFormat, timeFormat } from "../../../../helpers/date/date";
import { sendNotificationToken } from "../../../../api/fcm/notification";
import { suggestionBackgroundColor } from "../booking/style/BookingStyle";
import "../booking/style/booking.css";

function getOrderedSubRoutes(booking) {
  const subRoutes = booking?.subRoutes ? Object.values(booking.subRoutes) : [];
  const destination = booking?.destination;
  const orderedSubRoutes = subRoutes
    .filter((subRoute) => subRoute?.name)
    .filter(
      (subRoute) =>
        subRoute.name !== destination?.name ||
        subRoute.lat !== destination?.lat ||
        subRoute.lng !== destination?.lng,
    )
    .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));

  if (destination?.name) {
    orderedSubRoutes.push(destination);
  }

  return orderedSubRoutes;
}

function getDestinationValue(subRoutes) {
  if (!Array.isArray(subRoutes) || subRoutes.length === 0) return "";
  if (subRoutes.length === 1) return subRoutes[0].name || "";

  return `${subRoutes.length} paradas`;
}

function hasMultipleSubRoutes(subRoutes) {
  return Array.isArray(subRoutes) && subRoutes.length > 1;
}

function arrayMove(items, fromIndex, toIndex) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);

  return nextItems;
}

function getSortableSubRoutes(subRoutes) {
  return subRoutes.map((subRoute, index) => ({
    ...subRoute,
    sortableId: `sub-route-${subRoute.id || "destination"}-${index}`,
  }));
}

function getSubRoutesFromModalList(subRoutes) {
  return subRoutes.map(({ sortableId, ...subRoute }) => subRoute);
}

function getRouteDestination(route) {
  if (!route) return null;

  const { sortableId, id, ...destination } = route;
  return destination;
}

function getBookingRouteValues(subRoutes) {
  const routes = Array.isArray(subRoutes)
    ? subRoutes
        .map((route) => {
          if (!route) return null;

          const { sortableId, ...subRoute } = route;
          return subRoute;
        })
        .filter((route) => route?.name)
    : [];

  if (routes.length === 0) {
    return {
      destination: null,
      subRoutes: [],
    };
  }

  return {
    destination: getRouteDestination(routes[routes.length - 1]),
    subRoutes: routes.slice(0, -1).map((route, index) => ({
      ...route,
      id: index + 1,
    })),
  };
}

function getNextSubRouteId(subRoutes) {
  const ids = subRoutes.map((subRoute) => Number(subRoute.id || 0));
  return Math.max(0, ...ids) + 1;
}

function SubRouteSortableItem({ subRoute, index, onDelete }) {
  const { ref, handleRef, isDragging } = useSortable({
    id: String(subRoute.sortableId),
    index,
    group: "booking-sub-routes",
  });

  return (
    <Box
      ref={ref}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        minHeight: 44,
        px: 1,
        py: 0.75,
        border: "1px solid rgba(20, 83, 45, 0.16)",
        borderRadius: 1,
        bgcolor: isDragging ? "rgba(20, 83, 45, 0.08)" : "background.paper",
      }}
    >
      <IconButton ref={handleRef} size="small" sx={{ cursor: "grab" }}>
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {index + 1}. {subRoute.name}
      </Typography>
      <IconButton
        size="small"
        color="error"
        aria-label="eliminar parada"
        onClick={() => onDelete(subRoute.sortableId)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

const FormBooking = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [driver, setDriver] = useState(null);
  const { t } = useTranslation();
  const { id } = useParams();
  const [commissionOption, setCommissionOption] = useState(true);
  const [commissionValue, setCommissionValue] = useState(0);
  const [offers, setOffers] = useState([]);
  const [activePhone, setActivePhone] = useState(true);
  const [openSubRoutesModal, setOpenSubRoutesModal] = useState(false);
  const [subRoutesModalList, setSubRoutesModalList] = useState([]);
  const [subRouteSearch, setSubRouteSearch] = useState("");
  const [originError, setOriginError] = useState(false);
  const handleActivePhone = (event) => setActivePhone(event.target.checked);
  const handleCloseSubRoutesModal = () => setOpenSubRoutesModal(false);
  const handleOpenSubRoutesModal = (subRoutes) => {
    setSubRoutesModalList(getSortableSubRoutes(subRoutes));
    setSubRouteSearch("");
    setOpenSubRoutesModal(true);
  };
  const handleSubRoutesDragEnd = (event) => {
    const operation = event.operation;

    if (!operation || operation.canceled || !isSortableOperation(operation)) {
      return;
    }

    const sourceIndex = operation.source.initialIndex;
    const targetIndex = operation.source.index;
    if (sourceIndex === targetIndex) return;

    setSubRoutesModalList((currentList) =>
      arrayMove(currentList, sourceIndex, targetIndex),
    );
  };
  const handleDeleteSubRoute = (sortableId) => {
    setSubRoutesModalList((currentList) =>
      currentList.filter((subRoute) => subRoute.sortableId !== sortableId),
    );
  };
  const handleAddSubRoute = async (value) => {
    try {
      const result = await geocodeByAddress(value);
      const ll = await getLatLng(result[0]);

      setSubRoutesModalList((currentList) => {
        const id = getNextSubRouteId(currentList);
        const newSubRoute = {
          id,
          name: value,
          lat: ll.lat,
          lng: ll.lng,
          sortableId: `sub-route-${id}-${Date.now()}`,
        };

        return [...currentList, newSubRoute];
      });
      setSubRouteSearch("");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE AGREGO LA PARADA", "error");
    }
  };

  const dbRef = ref(db, `bookingNow/${id}`);
  const dbRefOffers = ref(db, `CounterOffer/Normal`);

  useEffect(() => {
    onValue(dbRef, (snapshot) => {
      let data = snapshot.val();
      if (data === null) return console.log("No Booking");
      setBooking({
        ...data,
        subRoutes: getOrderedSubRoutes(data),
      });
    });
  }, []);

  useEffect(() => {
    onValue(dbRefOffers, (snapshot) => {
      const data = snapshot.val();
      setOffers([]);
      if (data !== null) {
        Object.values(data).map((_book) => {
          if (_book.idBooking === id) {
            setOffers((oldArray) => [...oldArray, _book]);
          }
        });
      }
    });
  }, []);

  const [dateformat, setDateFormat] = useState("");
  const [timeFormatter, setTimeFormat] = useState("");
  useEffect(() => {
    if (booking) {
      if (booking.driver) {
        const driver = {
          brand: booking.driver.brand,
          color: booking.driver.color,
          driver_id: booking.driver.driver_id,
          lastname: booking.driver.lastname,
          model: booking.driver.model,
          name: booking.driver.name,
          bodywork: booking.driver.bodywork,
          plate: booking.driver.plate,
          vehicle_id: booking.driver.vehicle_id,
          percentageValue: booking.driver.percentageValue,
        };
        setCommissionValue(driver.percentageValue);
        setDriver(driver);
      }
      setActivePhone(booking.activePhone ? booking.activePhone : true);
      setDateFormat(dayjs(booking.date, "DD/MM/YYYY").toDate());
      setTimeFormat(formatterTimePicker(booking.date, booking.time));
    }
  }, [booking]);

  useEffect(() => {
    const driversData = async () => {
      const verify = verifyExpireTokenRefresh();
      if (verify) return console.log("no token");

      const token = getTokenRefresh();
      try {
        const response = await getDriverAndVehicle(token);
        setDrivers(response?.data);
      } catch (error) {
        console.log(error);
      }
    };
    driversData();
  }, []);

  const checkoutSchema = yup.object().shape({});

  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const handleClose = () => setStateSnk(false);

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const handleMouseDownPassword = (e) => e.preventDefault();

  const handleFormSubmit = async (values) => {
    if (!values.origin?.name?.trim()) {
      setOriginError(true);
      snackBarDialog("EL ORIGEN NO DEBE SER VACIO", "error");
      return;
    }

    const routeValues = getBookingRouteValues(values.subRoutes);

    if (!routeValues.destination) {
      snackBarDialog("Debe seleccionar un destino", "error");
      return;
    }

    setIsLoading(true);
    values.destination = routeValues.destination;
    values.subRoutes = routeValues.subRoutes;
    values.driver = driver;
    values.statusService = driver ? "ASIGNADO" : "PENDIENTE";
    values.statusPayment = "ASIGNADO";
    values.activePhone = activePhone;
    try {
      if (!commissionOption) {
        values.driver.percentageValue = commissionValue;
      }
      if (driver) {
        const token = "asdf";
        values.commission = values.driver.percentageValue;
        values.date = values.date.$d ? dateFormat(values.date.$d) : values.date;
        values.time = values.time.$d ? timeFormat(values.time.$d) : values.time;
        if (!values.isCreated) {
          if (values.payment_type === 2) values.statusBooking = "PAGADO";
          else values.statusBooking = "PENDIENTE";

          values.assigned = false;
          await createBookingNow(token, values);
          values.isCreated = true;

          const dbRefToken = ref(db, `Token/Driver/${driver.driver_id}`);
          get(dbRefToken)
            .then(async (snapshot) => {
              const data = snapshot.val();
              if (data != null) {
                const token = data.token;
                const title = "PEDIDO PARA AHORA CLIENTES";
                const body = "Pedido asignado";
                await sendNotificationToken(token, title, body);
              }
            })
            .catch((error) => {
              console.log(error);
            });
        } else {
          await updateDataBookingNow(token, values);
        }
        await update(ref(db, `bookingNow/${values.uuid}`), values);
        setIsLoading(false);
      } else {
        await update(ref(db, `bookingNow/${values.uuid}`), values);
        setIsLoading(false);
      }
      snackBarDialog("Pedido Actualizado");
      setTimeout(() => {
        navigate("/admin/list-book-now");
      }, 5000);
    } catch (error) {
      setIsLoading(false);
      snackBarDialog("Error: No Actualizado", "error");
    }
  };

  const asignDriver = async (idDriver, idRider) => {
    try {
      await update(dbRef, { idDriver: Number(idDriver) });
      await deleteOthersOffers();
      const dbRefToken = ref(db, `Token/Driver/${idDriver}`);
      const dbRefRider = ref(db, `Token/Client/${idRider}}`);

      const driverSnap = await get(dbRefToken);
      const tokenDriver = driverSnap.val();
      if (tokenDriver != null) {
        const token = tokenDriver.token;
        const title = "PEDIDO ACEPTADO";
        const body = "Pedido Aceptado";
        await sendNotificationToken(token, title, body);
      }

      const riderSnap = await get(dbRefRider);
      const tokenRider = riderSnap.val();
      if (tokenRider != null) {
        const token = tokenRider.token;
        const title = "CONDUCTOR ASIGNADO";
        const body = "Conductor asignado por el administrador de Taxi Mundo";
        await sendNotificationToken(token, title, body);
      }

      snackBarDialog("RESERVA ASIGNADA A CONDUCTOR");
    } catch (error) {
      console.log(error);
      snackBarDialog("ERROR: NO SE LOGRO ASIGNAR EL PEDIDO", "error");
    }
  };

  const deleteOthersOffers = async () => {
    try {
      Object.values(offers).map(async (_book) => {
        const dbRefDel = ref(db, `CounterOffer/Normal/${_book.idBooking}`);
        await remove(dbRefDel);
      });
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      {booking ? (
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={booking}
          validationSchema={checkoutSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => {
            const handleChangeOrigin = (value) => {
              setOriginError(false);
              setFieldValue("origin", {
                ...(values.origin || {}),
                name: value,
              });
            };
            const handleSelectOrigin = async (value) => {
              try {
                const result = await geocodeByAddress(value);
                const ll = await getLatLng(result[0]);

                setOriginError(false);
                setFieldValue("origin", {
                  ...(values.origin || {}),
                  name: value,
                  lat: ll.lat,
                  lng: ll.lng,
                });
              } catch (error) {
                console.log(error);
                snackBarDialog("ERROR: NO SE ACTUALIZO EL ORIGEN", "error");
              }
            };
            const handleSaveSubRoutesModal = () => {
              setFieldValue(
                "subRoutes",
                getSubRoutesFromModalList(subRoutesModalList),
              );
              handleCloseSubRoutesModal();
            };

            return (
              <form onSubmit={handleSubmit}>
              <Box backgroundColor={colors.primary[400]} p={1}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <PlacesAutocomplete
                      value={values.origin?.name || ""}
                      onChange={handleChangeOrigin}
                      onSelect={handleSelectOrigin}
                      searchOptions={{
                        componentRestrictions: { country: "pe" },
                      }}
                    >
                      {({
                        getInputProps,
                        suggestions,
                        getSuggestionItemProps,
                        loading,
                      }) => (
                        <Box sx={{ position: "relative" }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="text"
                            label={t("origin")}
                            color="success"
                            error={originError}
                            {...getInputProps({
                              name: "origin",
                              className: "location-search-input",
                            })}
                          />
                          <Box
                            className="autocomplete-dropdown-container suggestions"
                            sx={{ width: "100%" }}
                          >
                            {loading && <div>Loading...</div>}
                            {suggestions.map((suggestion) => {
                              const className = suggestion.active
                                ? "suggestion-item--active"
                                : "suggestion-item";
                              const style = suggestion.active
                                ? suggestionBackgroundColor(colors.primary[400])
                                : suggestionBackgroundColor(colors.primary[400]);

                              return (
                                <div
                                  key={suggestion.description}
                                  {...getSuggestionItemProps(suggestion, {
                                    className,
                                    style,
                                  })}
                                >
                                  <span>{suggestion.description}</span>
                                </div>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </PlacesAutocomplete>
                  </Grid>
                  {values.intermediate ? (
                    <Grid item xs={12} md={6}>
                      <TextField
                        value={
                          values.intermediate[0]
                            ? values.intermediate.map((inter) => {
                                return inter.service + ", ";
                              })
                            : null
                        }
                        fullWidth
                        variant="outlined"
                        color="success"
                        type="text"
                        label={t("Intermediate")}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        name="destination"
                        sx={{ gridColumn: "span 2" }}
                      />
                    </Grid>
                  ) : null}
                  <Grid item xs={12} md={6}>
                    <TextField
                      value={getDestinationValue(values.subRoutes)}
                      fullWidth
                      variant="outlined"
                      color="success"
                      type="text"
                      label={t("destination")}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      name="destination"
                      InputProps={{
                        readOnly: true,
                        onClick: () => {
                          if (hasMultipleSubRoutes(values.subRoutes)) {
                            handleOpenSubRoutesModal(values.subRoutes);
                          }
                        },
                      }}
                      sx={{
                        cursor: hasMultipleSubRoutes(values.subRoutes)
                          ? "pointer"
                          : "default",
                        "& .MuiInputBase-input": {
                          cursor: hasMultipleSubRoutes(values.subRoutes)
                            ? "pointer"
                            : "default",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={11} md={6}>
                    <TextField
                      value={values.clientName}
                      fullWidth
                      variant="outlined"
                      color="success"
                      type="text"
                      label={t("client")}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      name="clientName"
                      InputProps={{
                        endAdornment: (
                          <Checkbox
                            style={{ marginRight: -15 }}
                            checked={activePhone}
                            onChange={handleActivePhone}
                            color="success"
                          />
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3} md={3}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <Stack spacing={2}>
                            <MobileDatePicker
                              label={t("date")}
                              inputFormat="DD/MM/YYYY"
                              name="date"
                              value={dateformat}
                              onChange={(newValue) => {
                                setDateFormat(newValue);
                                values.date = newValue;
                              }}
                              renderInput={(params) => (
                                <TextField {...params} />
                              )}
                            />
                          </Stack>
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={6} sm={3} md={3}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <Stack spacing={3}>
                            <MobileTimePicker
                              label={t("time")}
                              value={timeFormatter}
                              name="time"
                              onChange={(newValue) => {
                                setTimeFormat(newValue);
                                values.time = newValue;
                              }}
                              renderInput={(params) => (
                                <TextField {...params} />
                              )}
                            />
                          </Stack>
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={6} sm={3} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>{t("Model")}</InputLabel>
                          <Select
                            value={values.vehicle}
                            id="vehicle"
                            name="vehicle"
                            label={t("vehicle")}
                            onChange={handleChange}
                          >
                            <MenuItem value={1}>{t("SEDAN")}</MenuItem>
                            <MenuItem value={2}>{t("SUV")}</MenuItem>
                            <MenuItem value={3}>{t("MINIVAN")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3} md={3}>
                        <TextField
                          value={values.persons}
                          fullWidth
                          variant="outlined"
                          color="success"
                          type="number"
                          label={t("persons")}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          name="persons"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <TextField
                      value={values.note}
                      fullWidth
                      variant="outlined"
                      color="success"
                      type="text"
                      label={t("notes")}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      name="note"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    {drivers ? (
                      <Autocomplete
                        value={driver}
                        disablePortal
                        name="driver"
                        id="combo-box-driver"
                        onChange={(e, newValue) => {
                          setDriver(newValue);
                        }}
                        options={drivers}
                        getOptionLabel={(option) =>
                          `${option.driver_id}: ${option.name} ${option.lastname} - ${option.brand} ${option.model} ${option.bodywork} ${option.plate} ${option.color}`
                        }
                        renderInput={(params) => (
                          <TextField {...params} label={t("driver")} />
                        )}
                      />
                    ) : (
                      <div>loading ...</div>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={3} md={3}>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={12} md={12}>
                        <FormControl fullWidth>
                          <InputLabel>{t("comprobante")}</InputLabel>
                          <Select
                            value={values.payment_voucher}
                            id="payment_voucher"
                            name="payment_voucher"
                            label={t("comprobante")}
                            onChange={handleChange}
                          >
                            <MenuItem value={1}>{t("RECIBO")}</MenuItem>
                            <MenuItem value={2}>{t("N/A")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={12} md={12}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel htmlFor="outlined-adornment-password">
                            COMISION
                          </InputLabel>
                          <OutlinedInput
                            value={commissionValue}
                            name="commissionValue"
                            onChange={(e) => {
                              setCommissionValue(e.target.value);
                            }}
                            disabled={commissionOption}
                            id="control-dni"
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onMouseDown={handleMouseDownPassword}
                                  edge="end"
                                  onClick={(e) => {
                                    setCommissionOption(!commissionOption);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </InputAdornment>
                            }
                            label="COMISION"
                            aria-describedby="outlined-helper-text"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={3} md={3}>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={12} md={12}>
                        <FormControl fullWidth>
                          <InputLabel>{t("Modeda")}</InputLabel>
                          <Select
                            value={values.currency}
                            id="currency"
                            name="currency"
                            label={t("Moneda")}
                            onChange={handleChange}
                          >
                            <MenuItem value={1}>{t("PEN")}</MenuItem>
                            <MenuItem value={2}>{t("USD")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={12} md={12}>
                        <FormControl fullWidth>
                          <InputLabel>{t("Tipo Pago")}</InputLabel>
                          <Select
                            value={values.payment_type}
                            id="payment_type"
                            name="payment_type"
                            label={t("payment")}
                            onChange={handleChange}
                          >
                            <MenuItem value={1}>{t("EFECTIVO")}</MenuItem>
                            <MenuItem value={2}>{t("CREDITO")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={3} md={3}>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={12} md={12}>
                        <TextField
                          value={values.tax}
                          fullWidth
                          variant="outlined"
                          color="success"
                          type="number"
                          label={t("tax")}
                          onChange={handleChange}
                          name="tax"
                          inputProps={{ style: { fontSize: 14 } }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={12} md={12}>
                        <TextField
                          value={values.toll}
                          fullWidth
                          variant="outlined"
                          color="success"
                          type="number"
                          label={t("toll")}
                          onChange={handleChange}
                          name="toll"
                          inputProps={{ style: { fontSize: 14 } }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={3} md={3}>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={12} md={12}>
                        <TextField
                          value={values.price}
                          fullWidth
                          variant="outlined"
                          color="success"
                          type="number"
                          label={t("Precio Conductor")}
                          onChange={handleChange}
                          name="price"
                          inputProps={{ style: { fontSize: 14 } }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={12} md={12} height={45}>
                        <TextField
                          value={values.total}
                          fullWidth
                          variant="outlined"
                          color="success"
                          type="number"
                          label={t("Precio Total")}
                          onChange={handleChange}
                          name="total"
                          inputProps={{ style: { fontSize: 14 } }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    {offers.length > 0 ? (
                      Object.values(offers).map((_book, index) => (
                        <Fragment key={index}>
                          <Card>
                            <Typography variant="h8" component="div">
                              {_book.nameDriver} {_book.bodywork} {_book.plate}{" "}
                              {_book.phoneDriver}, PEN:{_book.priceOffer} -
                              {_book.timeArrived}{" "}
                              <Button
                                color="secondary"
                                variant="contained"
                                disabled={isLoading}
                                onClick={() =>
                                  asignDriver(_book.idDriver, _book.client)
                                }
                              >
                                {t("ASIGNAR")}
                              </Button>
                            </Typography>
                          </Card>
                        </Fragment>
                      ))
                    ) : (
                      <Card>
                        <Typography variant="h5" textAlign={"center"}>
                          NO HAY OFERTAS DE CONDUCTORES
                        </Typography>
                      </Card>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Button
                      fullWidth
                      type="submit"
                      color="secondary"
                      variant="contained"
                      size="large"
                      sx={{ height: "100%" }}
                      disabled={isLoading}
                    >
                      GUARDAR CAMBIOS
                    </Button>
                  </Grid>
                </Grid>
                <SnackBar
                  stateSnk={stateSnk}
                  vertical={vertical}
                  horizontal={horizontal}
                  duration={4000}
                  handleClose={handleClose}
                  severity={severity}
                  messageSnk={messageSnk}
                />
                <Dialog
                  fullWidth
                  maxWidth="xs"
                  open={openSubRoutesModal}
                  onClose={handleSaveSubRoutesModal}
                  PaperProps={{
                    sx: {
                      height: "80vh",
                      maxHeight: "90vh",
                    },
                  }}
                >
                  <DialogTitle>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      sx={{ alignItems: { xs: "stretch", sm: "center" } }}
                    >
                      <Typography variant="h6" sx={{ flexShrink: 0 }}>
                        Paradas
                      </Typography>
                      <PlacesAutocomplete
                        value={subRouteSearch}
                        onChange={setSubRouteSearch}
                        onSelect={handleAddSubRoute}
                        searchOptions={{
                          componentRestrictions: { country: "pe" },
                        }}
                      >
                        {({
                          getInputProps,
                          suggestions,
                          getSuggestionItemProps,
                          loading,
                        }) => (
                          <Box sx={{ flex: 1, position: "relative" }}>
                            <TextField
                              fullWidth
                              size="small"
                              label={t("Destino")}
                              {...getInputProps({
                                className: "location-search-input",
                              })}
                            />
                            <Box
                              className="autocomplete-dropdown-container suggestionsDestination"
                              sx={{ width: "100%" }}
                            >
                              {loading && <div>Loading...</div>}
                              {suggestions.map((suggestion) => {
                                const className = suggestion.active
                                  ? "suggestion-item--active"
                                  : "suggestion-item";
                                const style = suggestion.active
                                  ? suggestionBackgroundColor(colors.primary[500])
                                  : suggestionBackgroundColor(colors.primary[300]);

                                return (
                                  <div
                                    key={suggestion.description}
                                    {...getSuggestionItemProps(suggestion, {
                                      className,
                                      style,
                                    })}
                                  >
                                    <span>{suggestion.description}</span>
                                  </div>
                                );
                              })}
                            </Box>
                          </Box>
                        )}
                      </PlacesAutocomplete>
                    </Stack>
                  </DialogTitle>
                  <DialogContent>
                    <Stack spacing={1} sx={{ pt: 1 }}>
                      <DragDropProvider onDragEnd={handleSubRoutesDragEnd}>
                        <Stack spacing={1}>
                          {subRoutesModalList.map((subRoute, index) => (
                            <SubRouteSortableItem
                              key={subRoute.sortableId}
                              subRoute={subRoute}
                              index={index}
                              onDelete={handleDeleteSubRoute}
                            />
                          ))}
                        </Stack>
                      </DragDropProvider>
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      type="button"
                      variant="contained"
                      color="error"
                      onClick={handleSaveSubRoutesModal}
                    >
                      {t("Cerrar")}
                    </Button>
                  </DialogActions>
                </Dialog>
              </Box>
              </form>
            );
          }}
        </Formik>
      ) : (
        <span>loading ...</span>
      )}
    </>
  );
};

export default FormBooking;